import React, { useEffect, useState } from 'react';
import Localize from 'react-intl-universal';
import { useNavigate } from 'react-router-dom';

import { alpha, Box, Paper } from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

import { useGlobalContext } from '@hooks/Context/GlobalContext';
import { routes } from '@navigation/Constants.js';

import ComponentHeader from '../ComponentHeader';
import ParticipantsList from './components/ParticipantsList';

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  paddingBottom: '0.3rem',
  fontWeight: '700',
  fontSize: '1.25rem',
  color: theme.palette.primary.contrastText,
  margin: '1.5rem 0'
}));

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    minHeight: '400px !important',
    backgroundColor: `${theme.palette.booking.background}!important`
  }
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: '1rem 3rem',
  [theme.breakpoints.down('sm')]: {
    padding: '0 0.5rem !important'
  }
}));

const StyledBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'start',
  alignItems: 'center',
  gap: '1rem',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column'
  }
}));

const StyledButton = styled(Button)(({ theme }) => ({
  fontWeight: 'bold !important',
  textTransform: 'none !important',
  fontSize: '1rem !important',
  minWidth: '13rem !important',
  color: theme.palette.common.white,
  [theme.breakpoints.down('sm')]: {
    mb: '1rem !important'
  }
}));

const StyledSessionList = styled(Button)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  padding: '0.7rem',
  margin: '8px',
  borderRadius: '0.5rem',
  backgroundColor: alpha(theme.palette.error.light, 0.04),
  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
    alignItems: 'center'
  }
}));

const StyledSessionListContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  justifyContent: 'center',
  gap: '1rem',
  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
    alignItems: 'center'
  },
  [theme.breakpoints.down('sm')]: {
    width: '100% !important',
    flexDirection: 'column',
    alignItems: 'flex-start',
    marginTop: '10px'
  }
}));

const BookingErrorDialog = ({
  eventData,
  availableSeats,
  availableSeatsErrors,
  setWaitingListApproved,
  participants,
  setParticipants,
  goToDetails,
  removeSession
}) => {
  const [open, setOpen] = useState(!!availableSeatsErrors);
  const [contextData] = useGlobalContext();

  const navigate = useNavigate();
  const [openParticipantList, setOpenParticipantList] = useState(false);

  const participantsWithoutBookingPerson = participants.filter(
    (participant) => !participant.isBookingPerson
  );
  const bookingPersonNotParticipate = participants.find((item) => item.isBookingPerson);

  const [selectedParticipants, setSelectedParticipants] = useState([]);

  const handleParticipantCheckboxChange = (id) => {
    setSelectedParticipants((prevSelected) =>
      prevSelected.includes(id) ? prevSelected.filter((item) => item !== id) : [...prevSelected, id]
    );
  };

  useEffect(() => {
    setOpen(!!availableSeatsErrors);
  }, [availableSeatsErrors]);

  return (
    <StyledDialog
      open={open}
      aria-labelledby='error-dialog-title'
      aria-describedby='error-dialog-description'
      fullWidth={true}
      maxWidth='md'
    >
      <StyledDialogTitle
        id='error-dialog-title'
        sx={{ mt: '1.5rem', pl: '3.5rem', pr: '3.5rem', pt: '2rem' }}
      >
        <ComponentHeader
          eventData={eventData}
          availableSeats={availableSeats?.count || 0}
          dialogHeader
        />
      </StyledDialogTitle>

      <StyledDialogContent>
        {!openParticipantList && (
          <Typography
            variant='body1'
            sx={{
              width: '100%',
              textAlign: 'center',
              fontWeight: 'bold',
              fontSize: '1.1rem',
              borderRadius: '0.5rem',
              padding: '0.5rem',
              mb: !availableSeatsErrors?.isSession ? '1.5rem' : '0.8rem',
              color: (theme) => alpha(theme.palette.error.contrastText, 0.8),
              backgroundColor: (theme) => alpha(theme.palette.error.light, 0.17)
            }}
          >
            {availableSeatsErrors?.noSpace
              ? Localize.get('Booking.ErrorDialog.eventNoSpace')
              : !availableSeatsErrors?.isSession
                ? Localize.get('Booking.ErrorDialog.onlyXSeatsAvailable', {
                    count: availableSeatsErrors?.seats,
                    pluralOrSingular:
                      availableSeatsErrors?.seats === 1
                        ? Localize.get('Booking.seat')
                        : Localize.get('Booking.seats')
                  })
                : availableSeatsErrors?.sessionGreaterThenEvent
                  ? Localize.get('Booking.ErrorDialog.sessionGreaterThenEvent')
                  : Localize.get('Booking.ErrorDialog.sessionErrors', {
                      count: participantsWithoutBookingPerson?.length,
                      pluralOrSingularParticipant:
                        participantsWithoutBookingPerson?.length === 1
                          ? Localize.get('Booking.participant')?.toLowerCase()
                          : Localize.get('Booking.participants')?.toLowerCase(),
                      pluralOrSingular:
                        availableSeatsErrors?.name?.length === 1 ? 'session' : 'sessions'
                    })}
          </Typography>
        )}
        {!openParticipantList && availableSeatsErrors?.isSession && (
          <Box mb={2}>
            {availableSeatsErrors?.name?.map((sessionName, index) => (
              <StyledSessionList component='div' key={availableSeatsErrors.ids[index]}>
                <Typography sx={{ flex: '1', p: '0.5rem' }}>{sessionName}</Typography>
                <StyledSessionListContainer>
                  {availableSeats === 0 ? (
                    Localize.get('Catalog.fullybooked')
                  ) : contextData?.isSeatsNumber ? (
                    <Typography color='error' minWidth={'110px'}>
                      {`${Localize.get('CourseDetails.availableSeats')}: ${availableSeatsErrors.seats[index]}`}
                    </Typography>
                  ) : (
                    Localize.get('Catalog.seatsAvailable')
                  )}

                  <StyledButton
                    sx={{ minWidth: '150px !important' }}
                    variant='contained'
                    onClick={() => setWaitingListApproved(availableSeatsErrors.ids[index])}
                    disabled={!availableSeatsErrors.confirmAndContinue[index]}
                  >
                    {availableSeatsErrors.confirmAndContinue[index]
                      ? Localize.get('Booking.ErrorDialog.bookWaitingList')
                      : `${Localize.get('Booking.ErrorDialog.bookWaitingList')}: ${Localize.get('Booking.off')}`}
                  </StyledButton>
                  <StyledButton
                    variant='contained'
                    sx={{ mr: 1, minWidth: 'fit-content !important' }}
                    onClick={() => removeSession(availableSeatsErrors.ids[index])}
                  >
                    {Localize.get('Booking.removeSession')}
                  </StyledButton>
                </StyledSessionListContainer>
              </StyledSessionList>
            ))}
          </Box>
        )}
        {openParticipantList ? (
          <>
            <Typography p={'0.5rem'}>
              {Localize.get('Booking.ErrorDialog.selectParticipants')}
            </Typography>
            <ParticipantsList
              participants={participantsWithoutBookingPerson}
              selectedParticipants={selectedParticipants}
              handleChange={handleParticipantCheckboxChange}
              bookingPersonNotParticipate={bookingPersonNotParticipate}
            />
          </>
        ) : (
          <>
            {availableSeatsErrors?.confirmAndContinue && !availableSeatsErrors?.isSession && (
              <Paper sx={{ padding: '1rem', marginBottom: '1rem' }}>
                <StyledBox>
                  <StyledButton onClick={() => setWaitingListApproved()} variant='contained'>
                    <Typography>
                      {Localize.get('Booking.ErrorDialog.confirmAndContinue')}
                    </Typography>
                  </StyledButton>
                  <Box sx={{ ml: '1rem', display: 'flex', flexDirection: 'column' }}>
                    {availableSeatsErrors?.noSpace && (
                      <Typography>{Localize.get('Booking.ErrorDialog.eventNoSpaceTop')}</Typography>
                    )}
                    <Typography>
                      {Localize.get('Booking.ErrorDialog.eventNoSpaceBottom')}
                    </Typography>
                  </Box>
                </StyledBox>
              </Paper>
            )}
            {!availableSeatsErrors?.noSpace && (
              <Paper
                sx={{
                  padding: '1rem',
                  marginBottom: '1rem',
                  mt: availableSeatsErrors?.isSession ? '1.5rem' : 0
                }}
              >
                <StyledBox>
                  <StyledButton
                    onClick={() => {
                      setSelectedParticipants(
                        participantsWithoutBookingPerson?.map((participant) => participant.id)
                      );
                      setOpenParticipantList(true);
                    }}
                    variant='contained'
                  >
                    <Typography>{Localize.get('Booking.removeParticipants')}</Typography>
                  </StyledButton>
                  <Typography ml={'1rem'}>
                    {Localize.get('Booking.clickToRemoveParticipants')}
                  </Typography>
                </StyledBox>
              </Paper>
            )}
          </>
        )}
      </StyledDialogContent>
      <DialogActions sx={{ backgroundColor: (theme) => theme.palette.booking.dialogFooter, p: 2 }}>
        {openParticipantList ? (
          <>
            <Button
              variant='outlined'
              sx={{ fontWeight: 'bold', textTransform: 'none', fontSize: '1.1rem' }}
              onClick={() => setOpenParticipantList(false)}
            >
              {Localize.get('Booking.goBack')}
            </Button>
            <Button
              variant='outlined'
              sx={{ fontWeight: 'bold', textTransform: 'none', fontSize: '1.1rem' }}
              disabled={participantsWithoutBookingPerson?.length === selectedParticipants?.length}
              onClick={() => {
                setOpenParticipantList(false);
                setParticipants([
                  ...(bookingPersonNotParticipate ? [bookingPersonNotParticipate] : []),
                  ...participantsWithoutBookingPerson.filter((participant) =>
                    selectedParticipants.includes(participant.id)
                  )
                ]);
              }}
            >
              {Localize.get('Booking.saveParticipants')}
            </Button>
          </>
        ) : (
          <Button
            variant='outlined'
            sx={{
              fontWeight: 'bold',
              textTransform: 'none',
              fontSize: '1.1rem',
              borderColor: (theme) => theme.palette.error.main,
              color: (theme) => theme.palette.error.dark,
              '&:hover': {
                backgroundColor: (theme) => alpha(theme.palette.error.light, 0.2),
                borderColor: (theme) => theme.palette.error.main
              }
            }}
            onClick={() =>
              navigate(goToDetails, {
                state: {
                  from: Localize.get('AppNavBar.catalog'),
                  path: routes.name.catalog
                }
              })
            }
          >
            {Localize.get('Booking.cancelBooking')}
          </Button>
        )}
      </DialogActions>
    </StyledDialog>
  );
};

export default BookingErrorDialog;
